import fp from 'lodash/fp';
import React from 'react';
import { Col, Grid, Row } from 'react-bootstrap';
import { connect } from 'react-redux';
import {
  branch,
  compose,
  lifecycle,
  renderComponent,
  withProps,
  withStateHandlers
} from 'recompose';
import { bindActionCreators } from 'redux';
import {
  isTeamManager,
  responsibleManagerAdded
} from '../../../../../services';
import {
  businessActions,
  businessCurrentTeamStatsSelector,
  businessSelector
} from '../../../../../state/modules/business';
import { currentUserSelector } from '../../../../../state/modules/user';
import { ConfirmationModal } from '../../../../components';
import Loading from '../../../loading';
import TrainingPlanEdit from './training-plan-edit';
import { filterNonLegacy } from '../../../../../common/cpd-category-filter';

export const TeamTrainingPlan = compose(
  connect(
    (state) => ({
      business: businessSelector(state),
      businessTeamStats: businessCurrentTeamStatsSelector(state),
      currentUser: currentUserSelector(state),
      industryCpdCategorys: filterNonLegacy(state.industry.cpdCategorys),
      isFetchingTeams: state.business.isFetchingTeams,
      isUpdating: state.business.team.isUpdating,
      setTrainingPlanSuccess: state.business.team.setTeamTrainingPlanSuccess
    }),
    (dispatch) => ({
      getTeamStats: bindActionCreators(
        businessActions.business.team.getTeamStats,
        dispatch
      ),
      saveTeamTrainingPlan: bindActionCreators(
        businessActions.business.team.setTeamTrainingPlan,
        dispatch
      )
    })
  ),
  withProps(({ business, currentUser }) => ({
    businessTeamId: fp.get('businessTeamId')(
      isTeamManager(currentUser, business)
    )
  })),
  withStateHandlers(
    {
      isSaved: false,
      hasFailed: false
    },
    {
      showSavedBadge: () => () => ({ isSaved: true }),
      hideSavedBadge: () => () => ({ isSaved: false }),
      showFailedBadge: () => () => ({ hasFailed: true }),
      hideFailedBadge: () => () => ({ hasFailed: false })
    }
  ),
  lifecycle({
    componentDidMount() {
      const { business, businessTeamId, getTeamStats } = this.props;

      getTeamStats(business.id, businessTeamId);
    },
    componentDidUpdate(prevProps) {
      const {
        setTrainingPlanSuccess,
        showSavedBadge,
        hideSavedBadge,
        showFailedBadge
      } = this.props;
      if (setTrainingPlanSuccess && !prevProps.setTrainingPlanSuccess) {
        if (setTrainingPlanSuccess.success) {
          showSavedBadge();
          setTimeout(() => hideSavedBadge(), 1000);
        } else {
          showFailedBadge();
        }
      }
    }
  }),
  branch(
    ({ isFetchingTeams, businessTeamStats }) =>
      isFetchingTeams || !businessTeamStats,
    renderComponent(Loading)
  ),
  withProps(({ businessTeamStats }) => ({
    teamTrainingPlan: businessTeamStats.stats.cpdCategorys,
    industryTeam: fp.first(businessTeamStats.stats.industryBusinessTeam),
    businessTeam: businessTeamStats.businessTeam,
    teamGoals: businessTeamStats.stats.goals
  })),
  withStateHandlers(
    {
      trainingPlanUpdateConfirmation: {
        show: false,
        data: undefined
      }
    },
    {
      showConfirmation: () => (data) => ({
        trainingPlanUpdateConfirmation: {
          show: true,
          data
        }
      }),
      hideConfirmation: () => () => ({
        trainingPlanUpdateConfirmation: {
          show: false,
          data: undefined
        }
      })
    }
  )
)(
  ({
    business,
    businessTeam,
    isUpdating,
    isSaved,
    hasFailed,
    teamTrainingPlan,
    industryTeam,
    saveTeamTrainingPlan,
    showConfirmation,
    hideConfirmation,
    trainingPlanUpdateConfirmation,
    industryCpdCategorys,
    currentUser,
    hideFailedBadge,
    teamGoals
  }) => (
    <Grid>
      <form>
        <Row className='no-gutter'>
          <Col xs={12}>
            <TrainingPlanEdit
              entityTrainingPlans={[
                {
                  onSave: ({
                    entity,
                    trainingPlan,
                    industryId,
                    industryEntity,
                    goals,
                    callback
                  }) => {
                    const trainingPlanWithEntityId = fp.map((plan) => ({
                      ...plan,
                      businessTeamId: entity.id
                    }))(trainingPlan);
                    if (
                      responsibleManagerAdded(
                        trainingPlanWithEntityId,
                        teamTrainingPlan,
                        industryCpdCategorys
                      )
                    ) {
                      showConfirmation({
                        confirmSave: () => {
                          saveTeamTrainingPlan({
                            cpdCategorys: trainingPlanWithEntityId,
                            businessId: business.id,
                            businessTeamId: entity.id,
                            userId: currentUser.id,
                            industryId: industryId,
                            industry: industryEntity,
                            goals
                          });
                          callback();
                        }
                      });
                    } else {
                      saveTeamTrainingPlan({
                        cpdCategorys: trainingPlanWithEntityId,
                        businessId: business.id,
                        businessTeamId: entity.id,
                        userId: currentUser.id,
                        industryId: industryId,
                        industry: industryEntity,
                        goals
                      });
                      callback();
                    }
                  },
                  isUpdating: isUpdating,
                  isSaved,
                  hasFailed,
                  entity: businessTeam,
                  industryEntity: industryTeam,
                  trainingPlan: teamTrainingPlan,
                  goals: fp.map(({ progressGoalId, points }) => ({
                    goalId: progressGoalId,
                    points
                  }))(teamGoals)
                }
              ]}
              entityTitle='Team Plan'
              hideFailedBadge={hideFailedBadge}
              industryCpdCategorys={industryCpdCategorys}
            />
          </Col>
        </Row>

        <Row className='no-gutter submit-row text-center'>
          <Col xs={12} sm={7} smOffset={5}>
            <ConfirmationModal
              id='confirm-update-training-plan'
              show={trainingPlanUpdateConfirmation.show}
              actions={[
                {
                  onConfirmAction: () => {
                    const { confirmSave } = trainingPlanUpdateConfirmation.data;
                    confirmSave();
                    hideConfirmation();
                  },
                  actionLabel: 'Yes'
                }
              ]}
              cancel={hideConfirmation}
              message={
                <span>
                  You’re about to assign <strong>{businessTeam.name}</strong>{' '}
                  Responsible Manager content as a part of its CPD training
                  plan. This content should only be completed by RG105
                  representatives. Do you wish to continue?
                </span>
              }
              headingText={<span>Update Training Plan</span>}
            />
          </Col>
        </Row>
      </form>
    </Grid>
  )
);



// WEBPACK FOOTER //
// ./src/containers/pages/app/account-settings/components/team-training-plan.js